/* eslint-disable @typescript-eslint/no-explicit-any */
import operationSignatures from '../../_constants/operationSignatures.json';
import { prePageCall } from '@marriott/mi-headless-utils';
import { GetServerSideProps } from 'next';

const { SHOP_UXL_CALL_SPLIT } = process.env;
//This returns a component which can be rendered in any product specific [[page.tsx]]
//to return the page template(Head, main, sections) along with the required react Components mentioned in model.json and
// load the CSS and JS libs required by static components from AEM.
import { useMemo, useReducer } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';
import { getDeployedEnvType, useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { Layout, respGridUtil } from '@marriott/mi-headless-utils';
import clsx from 'clsx';
import axios from 'axios';
import { inspect } from 'util';
import { AvailabilityCalendarModel, getProcessEnvs } from '@marriott/mi-shop-components/utils';
import { HQVContext, hqvInitialState, hqvReducer } from '@marriott/mi-shop-components';
import { useGlobalStore } from '@marriott/mi-store-utils';
import { DISABLE_DISTANCE } from '@marriott/mi-shop-components/constants';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';

const {
  MI_DATALAYER_APP_URL,
  NEXT_PUBLIC_DATALAYER_SITENAME,
  NEXT_PUBLIC_DATALAYER_PRODUCT,
  NEXT_PUBLIC_DATALAYER_APPLICATION,
  NEXT_PUBLIC_DATALAYER_TEMPLATE,
  NEXT_PUBLIC_DATALAYER_VARIATION,
  NEXT_PUBLIC_ORIENTATION_LANDSCAPE,
  NEXT_PUBLIC_ORIENTATION_PORTRAIT,
  NEXT_PUBLIC_PATHTYPE_NEW,
  NEXT_PUBLIC_PATHTYPE_LEGACY,
  NEXT_PUBLIC_SEARCH_SINGLE_RATE,
  NEXT_PUBLIC_RAM_TILE_NAME,
  NEXT_PUBLIC_PAGE_BREAKPT,
  NEXT_PUBLIC_SEARCH_MAPVIEW,
  NEXT_PUBLIC_SEARCH_LISTVIEW,
  NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
  NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
  IS_LOCAL_DEV,
  NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
  NEXT_PUBLIC_AEM_PATH,
  NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
  NEXT_PUBLIC_PREFIX,
  NEXT_PUBLIC_BULGARI_URL,
  GOOGLE_MAP_API_KEY,
  BAIDU_MAP_API_KEY,
  SESSION_GET_CALL_URL_CLIENT,
  NEXT_PUBLIC_HQV_DEBUG,
} = getProcessEnvs();

export default function AvailabilityCalendarPage({ model, pagePath, operationSignatures, isAuthorMode }) {
  const MiApolloClient = useNextMiApolloClient(operationSignatures, getDeployedEnvType());

  const { metaNames, metaProperties, title, staticComponentClientLibsCSS, staticComponentClientLibsJS, gridCss } =
    model;
  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);

  return (
    <ApolloProvider client={MiApolloClient}>
      <Layout
        metaNames={metaNames}
        metaProps={metaProperties}
        title={title}
        staticComponentClientLibsCSS={staticComponentClientLibsCSS}
        staticComponentClientLibsJS={staticComponentClientLibsJS}
        isAuthorMode={isAuthorMode}
        gridCss={gridCss || ''}
      >
        <section>
          <div className={clsx('mx-auto', 'max-w-7xl', 'sm:px-6', 'lg:px-8', 'sm:py-2', 'lg:py-6')}>
            <HQVProvidedResponsiveGrid respGridModel={respGridModel} pagePath={pagePath} />
          </div>
        </section>
      </Layout>
    </ApolloProvider>
  );
}

/**
 * Scoped HQV React Context provider. The provider MUST wrap both HQVWrapper (when authored on the
 * ADF page) and SearchFormComponent so both share the same Context store when
 * EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND is off. Both live inside the ResponsiveGrid AEM tree, so we
 * wrap just that subtree instead of the entire <Layout>.
 *
 * Scoping matters: hoisting the useReducer + Provider into the page component would force the
 * entire <Layout> (brand bar / hotel header / etc.) to re-render whenever HQV state changes (e.g.
 * modal open). That in turn caused HotelHeader to refetch PhoenixBookHotelHeaderData because its
 * `getWindowSession()`-derived `isSession` dep gets a fresh reference each render.
 *
 * The value object is also memoized on [hqvState, hqvDispatch] so Context consumers only rerun
 * when HQV state actually changes.
 */
function HQVProvidedResponsiveGrid({ respGridModel, pagePath }: { respGridModel: any; pagePath: string }) {
  const [hqvState, hqvDispatch] = useReducer(hqvReducer, hqvInitialState);
  const contextValue = useMemo(() => ({ hqvState, hqvDispatch }), [hqvState, hqvDispatch]);

  return (
    <HQVContext.Provider value={contextValue}>
      <ResponsiveGrid
        key={pagePath}
        {...respGridModel}
        model={respGridModel}
        pagePath={pagePath}
        itemPath="root/responsivegrid"
      />
    </HQVContext.Provider>
  );
}
const shopSpecificData = async (request, context, interceptorResponse) => {
  const { log } = global.loggerInstance('AvailabilityCalendar:');
  let sessionFetchedData: any = {};
  let selector: string;
  const isBulgariDomain = interceptorResponse?.requestAttributes?.globalParamObject?.bulgariFlag ?? false; //ToDo: read param from interceptorResponse , change hierarchy as needed

  // Find Adobe cookies matching the pattern kndctr_*_AdobeOrg_*
  const adobeCookies: string[] = [];
  if (request?.req?.cookies) {
    Object.keys(request?.req.cookies).forEach(cookieName => {
      if (cookieName.startsWith('kndctr_') && cookieName.includes('_AdobeOrg_')) {
        const cookieValue = request?.req.cookies?.[cookieName] ?? '';
        if (cookieValue) {
          adobeCookies.push(`${cookieName}=${cookieValue}`);
        }
      }
    });
  }

  const preHeader: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-request-id': request?.req?.headers['x-request-id'],
    Cookie: `sessionID=${request?.req?.cookies?.sessionID};UserIdToken=${
      request?.req?.cookies?.UserIdToken ? request?.req?.cookies?.UserIdToken : ''
    };device-characteristics=${request?.req?.cookies?.['device-characteristics'] ?? ''}${
      adobeCookies.length > 0 ? ';' + adobeCookies.join(';') : ''
    }`,
    'Accept-Language':
      interceptorResponse?.requestAttributes?.pageManagerRequest?.global?.localeKey?.toString().replaceAll('_', '-') ??
      '',
    'X-Host': request?.req?.headers && request?.req?.headers?.['x-host'] ? request?.req?.headers['x-host'] : '',
    Dtt: String(request?.req?.headers?.dtt === 'true' || request?.query?.dtt === 'true'),
  };
  const url = request?.req?.url.split('?')[1];
  let preprocessorURL = NEXT_PUBLIC_PRE_PROCESSING_ADF_URL;
  if (url) {
    preprocessorURL += `?${url}`;
  }
  log.debug(
    `[ADF-LOGS] PREPROCESSING URL Request Dtt flag, ${String(
      request?.req?.headers?.dtt === 'true' || request?.query?.dtt === 'true'
    )}`
  );
  log.debug(`[ADF-LOGS] PREPROCESSING URL Request, ${preprocessorURL}`);
  log.debug(`[ADF-LOGS] INTERCEPTOR RESPONSE, ${inspect(interceptorResponse)}`);
  log.debug(`[ADF-LOGS] HEADERS , ${inspect(preHeader)}`);
  log.debug(`[ADF-LOGS] CONTEXT VALUE, ${inspect(context)}`);
  if (IS_LOCAL_DEV?.toString() !== 'true') {
    try {
      const response = await axios.post(preprocessorURL, interceptorResponse, {
        headers: preHeader,
      });

      const redirectUri = response?.headers?.location || response?.headers?.['Location'];
      log.debug(inspect(`[ADF-LOGS] RESPONSE REDIRECT URI:${redirectUri}`));
      if (redirectUri && redirectUri.indexOf('/search/availabilityCalendar') === -1) {
        log.debug(inspect(`[ADF-LOGS] SESSION ERROR FETCHED DATA:${inspect(response?.data?.['sessionData'])}`));
        const redirectionErrorObj = {
          value: true,
          redirect: {
            redirect: {
              permanent: false,
              destination: redirectUri,
            },
          },
        };
        const error: any = {};
        error.error = redirectionErrorObj;
        log.debug(inspect(`[ADF-LOGS]  ERROR OBJECT:${inspect(error)}`));
        return error;
      }
      let sessionData = response?.data?.['sessionData'];
      if (!sessionData?.cacheData) {
        sessionData = { cacheData: sessionData };
      }

      sessionFetchedData = sessionData;
      selector =
        sessionFetchedData?.cacheData?.data?.AriesReservation?.brandCode ??
        sessionFetchedData?.data?.AriesReservation?.brandCode;
      log.debug(inspect(`[ADF-LOGS] SHOP CALL RESPONSE:${inspect(response)}`));
      log.debug(inspect(`[ADF-LOGS] SHOP CALL RESPONSE:${inspect(response?.data)}`));
      log.debug(inspect(`[ADF-LOGS] SESSION FETCHED DATA:${inspect(sessionData)}`));
      useGlobalStore.setState({ sessionData });
      log.debug(inspect(`[ADF-LOGS] RESPONSE HEADERS:${inspect(response?.headers)}`));
    } catch (error: any) {
      log.debug(inspect(`[ADF-LOGS] ERROR HEADERS:${inspect(error?.response?.headers)}`));
      const redirectUri = error?.response?.headers?.location || error?.response?.headers?.['Location'];
      log.debug(inspect(`[ADF-LOGS] ERROR REDIRECT URI:${redirectUri}`));

      if (redirectUri && redirectUri.indexOf('/search/availabilityCalendar') === -1) {
        log.debug(inspect(`[ADF-LOGS] SESSION ERROR FETCHED DATA:${inspect(error?.response?.data?.['sessionData'])}`));
        let sessionData = error?.response?.data?.['sessionData'];
        if (!sessionData?.cacheData) {
          sessionData = { cacheData: sessionData };
        }

        sessionFetchedData = sessionData;
        if (sessionData) {
          useGlobalStore.setState({ sessionData });
        }
        log.debug(inspect(error));
        const redirectionErrorObj = {
          value: true,
          redirect: {
            redirect: {
              permanent: false,
              destination: redirectUri,
            },
          },
        };
        error.error = redirectionErrorObj;
        log.debug(inspect(`[ADF-LOGS]  ERROR OBJECT:${inspect(error)}`));
        return error;
      } else {
        throw error;
      }
    }
  }
  const featureFlagLogo = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
    target: TARGETS.APP.SHOP_RENDERER,
    enableStream: true,
  });
  // Fetch ADF ZUSTAND feature flag
  const featureFlagAdfZustand = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND, {
    target: TARGETS.APP.SHOP_RENDERER,
    enableStream: true,
  });
  //Fetch concurFlow feature flag
  const featureFlagEnableConcurFlow = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ENABLE_CONCUR_FLOW, {
    target: TARGETS.APP.SHOP_RENDERER,
    enableStream: true,
  });
  return {
    // Explicit signal that this render is on the ADF (Availability Calendar) page. Threaded through
    // _app.tsx -> defineComponentMapping -> HQVWrapper so the shared HQVWrapper component can key
    // off the ADF feature flag (EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND) instead of the SERP one when
    // authored on this page. Only the ADF renderer page sets this to true.
    isAdfPage: true,
    featureFlagLogo,
    featureFlagAdfZustand,
    SHOP_UXL_CALL_SPLIT,
    operationSignatures,
    MI_DATALAYER_APP_URL,
    datalayerParams: {
      MI_DATALAYER_APP_URL,
      NEXT_PUBLIC_DATALAYER_SITENAME,
      NEXT_PUBLIC_DATALAYER_PRODUCT,
      NEXT_PUBLIC_DATALAYER_APPLICATION,
      NEXT_PUBLIC_DATALAYER_TEMPLATE,
      NEXT_PUBLIC_DATALAYER_VARIATION,
    },
    datalayerProperties: {
      NEXT_PUBLIC_ORIENTATION_LANDSCAPE,
      NEXT_PUBLIC_ORIENTATION_PORTRAIT,
      NEXT_PUBLIC_PATHTYPE_NEW,
      NEXT_PUBLIC_PATHTYPE_LEGACY,
      NEXT_PUBLIC_SEARCH_SINGLE_RATE,
      NEXT_PUBLIC_RAM_TILE_NAME,
      NEXT_PUBLIC_PAGE_BREAKPT,
      NEXT_PUBLIC_SEARCH_MAPVIEW,
      NEXT_PUBLIC_SEARCH_LISTVIEW,
    },
    sessionFetchedData,
    NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    NEXT_PUBLIC_AEM_PATH,
    selector,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    NEXT_PUBLIC_PREFIX,
    disableDistanceFromLocation: DISABLE_DISTANCE,
    isBulgariDomain: isBulgariDomain,
    NEXT_PUBLIC_BULGARI_URL,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY,
    SESSION_GET_CALL_URL_CLIENT,
    featureFlagEnableConcurFlow: featureFlagEnableConcurFlow ?? null,
    NEXT_PUBLIC_HQV_DEBUG,
  };
};

const removeSearchFormItemsFromModel = (
  model: { cqItems: { root: { [x: string]: { responsivegrid: { [x: string]: any } } } } },
  searchFormItemsToRemove: string[]
) => {
  const items = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'];
  for (const item in items) {
    const itemType = items[item]?.[':type'];
    if (itemType && searchFormItemsToRemove.some(searchFormItemType => itemType === searchFormItemType)) {
      delete items[item];
    }
  }

  return model;
};

const shopSpecificModelOperation = (context, request, interceptorResponse, model, productSpecificProps) => {
  const { log } = global.loggerInstance('AvailabilityCalendar:');

  log.debug(`[ADF-LOGS-shopSpecificModelOperation] CONTEXT VALUE, ${inspect(context)}`);
  log.debug(`[ADF-LOGS-shopSpecificModelOperation] REQUEST VALUE, ${inspect(request)}`);
  let modelData = model;

  //read the param from interceptorResponse to determine if its Bulgari domain or not
  const isBulgariDomain = interceptorResponse?.requestAttributes?.globalParamObject?.bulgariFlag ?? false; //ToDo: read param from interceptorResponse , change hierarchy as needed
  const itemTypesToRemoveForBulgariDomain = [
    'mi-aem-common-spa/components/common/staticwrapper',
    'mi-aem-common-spa/components/experiencefragment',
  ]; // pass appropriate component names from AEM to be removed from model for Bulgari domain
  const itemTypesToRemoveForDefaultDomain = ['']; // pass appropriate component names from AEM to be removed from model for Default domain

  const { sessionFetchedData } = productSpecificProps;
  const { AriesSearch } = sessionFetchedData?.cacheData?.data ?? sessionFetchedData?.data ?? {};
  const searchFormExperience = AriesSearch?.searchFormExperience ?? '';
  let searchFormItemsToRemove = [];
  if (searchFormExperience === 'ngsf') {
    searchFormItemsToRemove = ['mi-aem-shop-spa/components/content/searchform/v1/searchform'];
  } else {
    searchFormItemsToRemove = ['mi-aem-shop-spa/components/content/ngsearchform/v1/ngsearchform'];
  }

  modelData = removeSearchFormItemsFromModel(modelData, searchFormItemsToRemove);

  modelData =
    //call tranformer to remove components for Bulgari/Default domain based on the isBulgariDomain flag
    modelData = transformModelIfBulgari(
      modelData,
      isBulgariDomain,
      itemTypesToRemoveForBulgariDomain,
      itemTypesToRemoveForDefaultDomain
    );
  return modelData;
};

//function to tranform the model.json based on the domain , if its bulgari or not.
const transformModelIfBulgari = (
  model: { cqItems: { root: { [x: string]: { responsivegrid: { [x: string]: any } } } } },
  isBulgariDomain = false,
  itemsToRemoveForBulgari: string | string[],
  itemsToRemoveForDefault: string | string[]
) => {
  const items = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'];
  for (const item in items) {
    //if bulgari domain , delete non bulgari header and footer
    if (isBulgariDomain) {
      if (
        itemsToRemoveForBulgari.includes(items[item]?.[':type']) &&
        (items[item]?.[':type'] === 'mi-aem-common-spa/components/common/staticwrapper' ||
          (items[item]?.[':type'] === 'mi-aem-common-spa/components/experiencefragment' &&
            items[item]?.localizedFragmentVariationPath?.includes('header')))
      ) {
        delete items[item];
      }
      if (
        items[item]?.['localizedFragmentVariationPath']?.includes('experience-fragments') &&
        !items[item]?.['localizedFragmentVariationPath']?.includes('bulgari-slim-footer')
      ) {
        delete items[item];
      }
    } else {
      //if not bulgari domain , delete bulgari header and footer
      if (itemsToRemoveForDefault.includes(items[item]?.[':type'])) {
        delete items[item];
      }
      if (
        items[item]?.['localizedFragmentVariationPath']?.includes('experience-fragments') &&
        items[item]?.['localizedFragmentVariationPath']?.includes('bulgari-slim-footer')
      ) {
        delete items[item];
      }
    }
  }

  return model;
};

//SSR Render
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  shopSpecificData,
  AvailabilityCalendarModel,
  false,
  undefined,
  undefined,
  undefined,
  shopSpecificModelOperation
);
